<template>
  <div>
    <StudentsConditionsComponent class="mb-2"
      v-if="!isConfirm"
      :initialConditions="conditions"
      @changeConditions="changeConditions" />

    <div v-else class="row container">
      <div class="col-4 form-group" v-for="condition of conditionsOnConfirm" :key="condition.key">
        <label>{{ condition.title }}</label>
        <div>{{ condition.value }}</div>
      </div>
    </div>

    <div>
      <PagerComponent v-model="pager" @changePage="changePage" class="mb-2" />
      <b-table striped hover fixed Outlined show-empty
        :items="students"
        :fields="fields"
        :empty-text="'No data available'"
        :table-class="'list-table text-center'"
        :thead-class="'list-table__head'"
        :tbody-class="'list-table__body'"
      />
    </div>

    <div v-if="!isConfirm" class="d-flex justify-content-end">
      <b-button pill variant="secondary" class="mr-2" @click="back">
        Back
      </b-button>
      <b-button pill variant="primary" @click="next">
        Next
      </b-button>
    </div>

    <Loading :isLoading="isLoading" />
  </div>
</template>

<script lang="ts">
import _ from 'lodash'
import { Component, Prop, Vue } from 'vue-property-decorator'
import { Student, StudentConditions } from '@/store/students/types'
import { RootState } from '@/store/types'
import { Pager } from '@/store/pager.types'
import {
  Campus,
  Level,
  Semester,
  University,
} from '@/store/master/types'
import { periodType, weekdayKey, Weekdays } from '@/store/common.types'
import StudentsConditionsComponent from '@/components/parts/mails/sendBulk/StudentsConditions.vue'
import PagerComponent from '@/components/parts/Pager.vue'
import Loading from '@/components/parts/modal/Loading.vue'

interface ConfditionMapping {
  title: string
  formatter?: Function
}

@Component({
  components: {
    StudentsConditionsComponent,
    PagerComponent,
    Loading,
  },
})
export default class MailsSendBulkSelectStudents extends Vue {
  @Prop({ required: true }) value!: StudentConditions|undefined

  @Prop({ default: false }) isConfirm!: boolean

  private isLoading: boolean = false

  private fields = [
    {
      key: 'studentNumber',
      label: 'Student number',
    },
    {
      key: 'name',
      label: 'Name (roman)',
    },
    {
      key: 'nameJp',
      label: 'Name',
    },
    {
      key: 'email1',
      label: 'Email',
    },
  ]

  private get students(): Student[] {
    return (this.$store.state as RootState).students.students
  }

  private get pager(): Pager {
    return (this.$store.state as RootState).students.pager
  }

  private get conditions(): StudentConditions|undefined {
    return this.value
  }

  private set conditions(conditions: StudentConditions|undefined) {
    this.$emit('input', conditions)
  }

  private get conditionsOnConfirm() {
    const conditionsMapping: { [name: string]: {
      title: string,
      formatter?: Function,
    }} = {
      fiscalYear: {
        title: 'Fiscal year',
      },
      semester: {
        title: 'Semester',
        formatter: (s: Semester): string => s.name,
      },
      university: {
        title: 'University',
        formatter: (u: University): string => u.name,
      },
      studentNumber: {
        title: 'Student number',
      },
      studentNameJapanese: {
        title: 'Student name',
      },
      studentName: {
        title: 'Student name (roman characters)',
      },
      email: {
        title: 'Email',
      },
      courseName: {
        title: 'Course name',
      },
      levels: {
        title: 'Levels',
        formatter: (ls: Level[]): string => ls.map((l) => l.name).join(', '),
      },
      dayOfWeeks: {
        title: 'Day of weeks',
        formatter: (w: weekdayKey): string => Weekdays[w],
      },
      periods: {
        title: 'Periods',
        formatter: (ps: periodType[]) => ps.join(', '),
      },
      tutorId: {
        title: 'Tutor id',
      },
      tutorName: {
        title: 'Tutor name',
      },
      campuses: {
        title: 'Campuses',
        formatter: (cs: Campus[]): string => cs.map((c) => c.name).join(', '),
      },
      classroom: {
        title: 'Classroom',
      },
      booth: {
        title: 'Booth',
      },
    }

    return _.chain(this.conditions)
      .pickBy((v) => v !== undefined && v !== null && !_.isEmpty(v))
      .map((value, key) => {
        const mapping = conditionsMapping[key]
        return {
          key,
          title: mapping.title,
          value: mapping.formatter ? mapping.formatter(value) : value,
        }
      })
      .value()
  }

  private created() {
    this.search(this.conditions)
  }

  /**
   * 履修者を検索
   */
  private search(conditions: StudentConditions|undefined, page: number = 1): void {
    if (this.isLoading) {
      return
    }
    this.isLoading = true
    this.$store.dispatch('fetchStudents', { conditions, page })
      .then(() => {
        this.conditions = conditions
      })
      .finally(() => {
        this.isLoading = false
      })
  }

  /**
   * 履修者検索条件変更
   */
  private changeConditions(conditions: StudentConditions): void {
    this.search(conditions)
  }

  /**
   * ページ変更
   */
  private changePage(page: number): void {
    this.search(this.conditions, page)
  }

  private back() {
    this.$emit('back')
  }

  private next() {
    const limit = 5000
    const count = (this.$store.state as RootState).students.pager.totalLength
    if (count > limit) {
      this.$bvModal.msgBoxOk(`Please resetting conditions under ${limit} students`)
    } else if (count === 0) {
      this.$bvModal.msgBoxOk('Selected 0 students, Please resetting conditions 1 or more students')
    } else {
      this.$emit('next')
    }
  }
}
</script>

<style scoped>
.list-table {
  word-break : break-all;
}
</style>
